import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import '../pm/theme.css';
import { AppUser, Project, ProjectDepartment, ProjectHealth, Task, TaskStatus, Workstream } from '../pm/types';
import PmSuiteLayout from '../components/pm/PmSuiteLayout';
import DepartmentPicker from '../components/pm/DepartmentPicker';
import HealthPill from '../components/pm/HealthPill';
import { computeHealth } from '../pm/health';
import {
  createWorkstream,
  deleteProject,
  reorderTasks,
  reorderWorkstreams,
  saveProjectAsTemplate,
  subscribeAllUsers,
  subscribeProjects,
  subscribeTasks,
  subscribeWorkstreams,
  updateProject,
} from '../pm/firebase';
import StatCards from '../components/pm/StatCards';
// FilterBar was a horizontal selector row living between StatCards and the
// workstreams; its owner+status selectors now live inside ProjectFilterMenu,
// rendered as a popout inside the view-bar row above the workstreams.
import ProjectFilterMenu from '../components/pm/ProjectFilterMenu';
import WorkstreamCard from '../components/pm/WorkstreamCard';
import ViewSelect, { PmView } from '../components/pm/ViewSelect';
import KanbanView from '../components/pm/KanbanView';
import GanttView from '../components/pm/GanttView';
import ProjectPriorityPicker, { PriorityChoice } from '../components/pm/ProjectPriorityPicker';
import { linkify } from '../pm/linkify';
import { dateInputToMillis, millisToDateInput } from '../pm/dueDate';
import { readTaskHash } from '../pm/share';
import { getCurrentUser } from '../firebase';

// Path segments that share the /Projects/:projectId shape but are actually
// tab views handled by ProjectsList. IonRouterOutlet can mount multiple
// matching routes into its page stack, so this guard prevents ProjectDetail
// from rendering its "Project not found" empty state when the URL is really
// /Projects/Roadmap, /Projects/MyWork, or /Projects/Templates.
const PROJECT_TAB_SEGMENTS = new Set(['Roadmap', 'MyWork', 'Templates']);

/* Inline edit-on-click for the project title. Reverts on Escape, saves on
 * blur or Enter. Lives here (not in a separate component file) because it's
 * only ever used inside this page and pulls in no extra dependencies. */
interface EditableTitleProps {
  value: string;
  onCommit: (next: string) => void | Promise<void>;
}
const EditableTitle: React.FC<EditableTitleProps> = ({ value, onCommit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-sync the draft if the underlying value changes from outside (e.g. a
  // realtime subscription update lands while not editing).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function commit() {
    setEditing(false);
    if (draft.trim() && draft.trim() !== value) onCommit(draft);
    else setDraft(value);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="pm-editable-title-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
      />
    );
  }
  return (
    <h1
      className="pm-editable-title"
      role="button"
      tabIndex={0}
      title="Click to rename"
      onClick={() => setEditing(true)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditing(true); } }}
    >
      {value}
    </h1>
  );
};

/* Same idea for the description — multiline, lazy-loaded into edit mode.
 * Shows an "Add a description" placeholder click target when the field is
 * blank so users can discover the affordance. */
interface EditableDescriptionProps {
  value: string;
  onCommit: (next: string) => void | Promise<void>;
  linkify: (s: string) => React.ReactNode;
}
const EditableDescription: React.FC<EditableDescriptionProps> = ({ value, onCommit, linkify }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [editing]);

  function commit() {
    setEditing(false);
    if (draft.trim() !== value.trim()) onCommit(draft);
    else setDraft(value);
  }

  if (editing) {
    return (
      <textarea
        ref={textareaRef}
        className="pm-editable-desc-input"
        value={draft}
        rows={Math.max(2, Math.min(8, draft.split('\n').length))}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          // Cmd/Ctrl+Enter commits (Enter alone adds newlines in a textarea).
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            setDraft(value);
            setEditing(false);
          }
        }}
        placeholder="Add a description (Cmd+Enter to save)"
      />
    );
  }
  if (!value) {
    return (
      <p
        className="pm-editable-desc pm-editable-desc-empty"
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditing(true); } }}
      >
        + Add a description
      </p>
    );
  }
  return (
    <p
      className="pm-editable-desc"
      role="button"
      tabIndex={0}
      title="Click to edit"
      onClick={() => setEditing(true)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditing(true); } }}
    >
      {linkify(value)}
    </p>
  );
};

const ProjectDetail: React.FC = () => {
  const history = useHistory();
  const { projectId } = useParams<{ projectId: string }>();

  const [uid, setUid] = useState<string>('');
  const [authLoading, setAuthLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [workstreams, setWorkstreams] = useState<Workstream[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  // PM mention pool is restricted to Rollover Rep Admins on purpose —
  // Projects is an RR-internal surface. Owners are admins too.
  const [admins, setAdmins] = useState<AppUser[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [newWorkstream, setNewWorkstream] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const [dropPos, setDropPos] = useState<'before' | 'after' | null>(null);
  // Task drag state — hoisted to project level so tasks can be moved across
  // workstreams.
  const [tDragId, setTDragId] = useState<string | null>(null);
  const [tDropTaskId, setTDropTaskId] = useState<string | null>(null);
  const [tDropPos, setTDropPos] = useState<'before' | 'after' | null>(null);
  const [tDropWsId, setTDropWsId] = useState<string | null>(null); // hovering empty area of a workstream
  const [addingWs, setAddingWs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Deep-link target — whatever the URL hash currently points at. Drives
  // workstream auto-expand and the transient highlight on TaskRow (CSS
  // animation handles the fade-out, so we don't need a separate timer state).
  const [targetTaskId, setTargetTaskId] = useState<string | null>(null);
  // Active view (List / Kanban / Gantt). Persisted per-project in localStorage
  // so users land back where they were when they re-enter.
  const [view, setViewState] = useState<PmView>('list');
  const viewStorageKey = `pm:project:${projectId}:view`;

  useEffect(() => {
    getCurrentUser()
      .then((u: any) => {
        setUid(u?.uid || '');
        setAuthLoading(false);
      })
      .catch(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    const unsubP = subscribeProjects(setProjects);
    const unsubW = subscribeWorkstreams(setWorkstreams);
    const unsubT = subscribeTasks(setTasks);
    const unsubA = subscribeAllUsers((users) => setAdmins(users.filter((u) => u.isOperator)));
    return () => {
      unsubP();
      unsubW();
      unsubT();
      unsubA();
    };
  }, []);

  // Read #task-{id} from the URL on mount and update on hashchange so links
  // pasted into the running tab also work.
  useEffect(() => {
    setTargetTaskId(readTaskHash());
    const onHash = () => setTargetTaskId(readTaskHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Restore the user's last view for this project. Deep-linking to a specific
  // task overrides — the list view is where the scroll-to-task UX lives.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(viewStorageKey);
      if (stored === 'kanban' || stored === 'gantt' || stored === 'list') {
        setViewState(stored);
      }
    } catch { /* ignore */ }
  }, [viewStorageKey]);

  useEffect(() => {
    if (targetTaskId) setViewState('list');
  }, [targetTaskId]);

  function setView(next: PmView) {
    setViewState(next);
    try { window.localStorage.setItem(viewStorageKey, next); } catch { /* ignore */ }
  }

  const project = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);

  const allProjectWorkstreams = useMemo(
    () => workstreams.filter((w) => w.projectId === projectId).sort((a, b) => a.sortOrder - b.sortOrder),
    [workstreams, projectId],
  );

  const projectWorkstreams = useMemo(
    () => allProjectWorkstreams.filter((w) => showArchived || !w.archived),
    [allProjectWorkstreams, showArchived],
  );

  const archivedCount = allProjectWorkstreams.filter((w) => w.archived).length;

  const projectTasks = useMemo(() => tasks.filter((t) => t.projectId === projectId), [tasks, projectId]);

  const filteredTasks = useMemo(() => {
    return projectTasks.filter((t) => {
      // A deep-linked target task always survives the filter — otherwise the
      // share link could land on a page where the task isn't visible at all.
      if (targetTaskId && t.id === targetTaskId) return true;
      if (ownerFilter === 'unassigned') {
        if (t.ownerId) return false;
      } else if (ownerFilter !== 'all' && t.ownerId !== ownerFilter) {
        return false;
      }
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      return true;
    });
  }, [projectTasks, ownerFilter, statusFilter, targetTaskId]);

  // If the deep-linked task lives in an archived workstream, surface it.
  useEffect(() => {
    if (!targetTaskId || showArchived) return;
    const t = projectTasks.find((tt) => tt.id === targetTaskId);
    if (!t) return;
    const ws = allProjectWorkstreams.find((w) => w.id === t.workstreamId);
    if (ws?.archived) setShowArchived(true);
  }, [targetTaskId, projectTasks, allProjectWorkstreams, showArchived]);

  // Once the deep-linked task is in the DOM, scroll it into view. The flash
  // itself is a CSS animation on the .pm-task-highlighted class, so we don't
  // need to manage a timer here. Retry briefly because the workstream may
  // need a render cycle to expand.
  useEffect(() => {
    if (!targetTaskId) return;
    if (!projectTasks.some((t) => t.id === targetTaskId)) return;
    let cancelled = false;
    let attempts = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(`task-${targetTaskId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (attempts++ < 30) window.setTimeout(tick, 50);
    };
    tick();
    return () => { cancelled = true; };
  }, [targetTaskId, projectTasks]);

  const filteredTasksByWorkstream = useMemo(() => {
    const m: Record<string, Task[]> = {};
    for (const t of filteredTasks) {
      if (!m[t.workstreamId]) m[t.workstreamId] = [];
      m[t.workstreamId].push(t);
    }
    return m;
  }, [filteredTasks]);

  const tasksByWorkstreamUnfiltered = useMemo(() => {
    const m: Record<string, Task[]> = {};
    for (const t of projectTasks) {
      if (!m[t.workstreamId]) m[t.workstreamId] = [];
      m[t.workstreamId].push(t);
    }
    return m;
  }, [projectTasks]);

  async function handleAddWorkstream(e: React.FormEvent) {
    e.preventDefault();
    const title = newWorkstream.trim();
    if (!title) return;
    setAddingWs(true);
    setError(null);
    try {
      const nextSort = allProjectWorkstreams.length;
      await createWorkstream(projectId, title, nextSort);
      setNewWorkstream('');
    } catch (err: any) {
      setError(err?.message || 'Could not create workstream.');
    } finally {
      setAddingWs(false);
    }
  }

  async function handleDeleteProject() {
    if (!project) return;
    if (!window.confirm(`Delete project "${project.title}" and all its workstreams and tasks?`)) return;
    try {
      await deleteProject(projectId, projectTasks, allProjectWorkstreams);
      history.push('/Projects');
    } catch (err: any) {
      setError(err?.message || 'Could not delete project.');
    }
  }

  // ---------- workstream drag-and-drop ----------

  function handleWsDragStart(id: string) {
    setDragId(id);
  }
  function handleWsDragOver(id: string, position: 'before' | 'after') {
    if (id !== dropId) setDropId(id);
    if (position !== dropPos) setDropPos(position);
  }
  function handleWsDragLeave(id: string) {
    if (dropId === id) {
      setDropId(null);
      setDropPos(null);
    }
  }
  function handleWsDragEnd() {
    setDragId(null);
    setDropId(null);
    setDropPos(null);
  }
  async function handleWsDrop(targetId: string) {
    const draggedId = dragId;
    const position = dropPos;
    setDragId(null);
    setDropId(null);
    setDropPos(null);
    if (!draggedId || !position || draggedId === targetId) return;

    const ordered = [...allProjectWorkstreams].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
    const dragged = ordered.find((w) => w.id === draggedId);
    if (!dragged) return;

    const without = ordered.filter((w) => w.id !== draggedId);
    const targetIdx = without.findIndex((w) => w.id === targetId);
    if (targetIdx === -1) return;
    const insertAt = position === 'before' ? targetIdx : targetIdx + 1;
    const reordered = [
      ...without.slice(0, insertAt),
      dragged,
      ...without.slice(insertAt),
    ];

    // Renumber sortOrders sequentially and persist only what changed.
    const writes = reordered
      .map((w, i) => ({ id: w.id, sortOrder: i, current: w.sortOrder }))
      .filter((x) => x.current !== x.sortOrder)
      .map((x) => ({ id: x.id, sortOrder: x.sortOrder }));

    if (writes.length === 0) return;
    try {
      await reorderWorkstreams(writes);
    } catch (err: any) {
      setError(err?.message || 'Could not reorder workstreams.');
    }
  }

  // ---------- task drag-and-drop (cross-workstream) ----------

  function handleTaskDragStart(id: string) {
    setTDragId(id);
  }
  function handleTaskDragOverTask(id: string, position: 'before' | 'after') {
    if (id !== tDropTaskId) setTDropTaskId(id);
    if (position !== tDropPos) setTDropPos(position);
    if (tDropWsId) setTDropWsId(null);
  }
  function handleTaskDragLeaveTask(id: string) {
    if (tDropTaskId === id) {
      setTDropTaskId(null);
      setTDropPos(null);
    }
  }
  function handleTaskDragOverWs(wsId: string) {
    // Only fires when the task isn't already hovered over a specific task.
    if (tDropTaskId) return;
    if (wsId !== tDropWsId) setTDropWsId(wsId);
  }
  function handleTaskDragLeaveWs(wsId: string) {
    if (tDropWsId === wsId) setTDropWsId(null);
  }
  function handleTaskDragEnd() {
    setTDragId(null);
    setTDropTaskId(null);
    setTDropPos(null);
    setTDropWsId(null);
  }

  async function doTaskMove(
    draggedId: string,
    targetWsId: string,
    // When dropping on a task, position is the slot relative to that task.
    // When dropping on a workstream's empty area, the task goes to the END.
    relativeTo: { kind: 'task'; taskId: string; position: 'before' | 'after' } | { kind: 'end' },
  ) {
    const dragged = tasks.find((t) => t.id === draggedId);
    if (!dragged) return;

    // Build current order for source and target workstreams.
    const sortByOrder = (a: Task, b: Task) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    const sourceWsId = dragged.workstreamId;
    const sameWs = sourceWsId === targetWsId;

    const targetWsTasks = tasks
      .filter((t) => t.workstreamId === targetWsId && t.id !== draggedId)
      .sort(sortByOrder);

    let insertAt: number;
    if (relativeTo.kind === 'end') {
      insertAt = targetWsTasks.length;
    } else {
      const idx = targetWsTasks.findIndex((t) => t.id === relativeTo.taskId);
      if (idx === -1) return;
      insertAt = relativeTo.position === 'before' ? idx : idx + 1;
    }

    const newTargetWs: Task[] = [
      ...targetWsTasks.slice(0, insertAt),
      { ...dragged, workstreamId: targetWsId },
      ...targetWsTasks.slice(insertAt),
    ];

    type Write = { id: string; sortOrder: number; workstreamId?: string };
    const writes: Write[] = [];

    // Renumber target workstream
    newTargetWs.forEach((t, i) => {
      const orig = tasks.find((x) => x.id === t.id);
      if (!orig) return;
      const wsChanged = orig.workstreamId !== targetWsId;
      const orderChanged = orig.sortOrder !== i;
      if (wsChanged || orderChanged) {
        const w: Write = { id: t.id, sortOrder: i };
        if (wsChanged) w.workstreamId = targetWsId;
        writes.push(w);
      }
    });

    // Renumber source workstream (without the moved task) if it's a different one.
    if (!sameWs) {
      const newSourceWs = tasks
        .filter((t) => t.workstreamId === sourceWsId && t.id !== draggedId)
        .sort(sortByOrder);
      newSourceWs.forEach((t, i) => {
        if (t.sortOrder !== i) writes.push({ id: t.id, sortOrder: i });
      });
    }

    if (writes.length === 0) return;
    try {
      await reorderTasks(writes);
    } catch (err: any) {
      setError(err?.message || 'Could not move task.');
    }
  }

  async function handleTaskDropOnTask(targetTaskId: string) {
    const draggedId = tDragId;
    const position = tDropPos;
    setTDragId(null);
    setTDropTaskId(null);
    setTDropPos(null);
    setTDropWsId(null);
    if (!draggedId || !position || draggedId === targetTaskId) return;
    const targetTask = tasks.find((t) => t.id === targetTaskId);
    if (!targetTask) return;
    await doTaskMove(draggedId, targetTask.workstreamId, {
      kind: 'task', taskId: targetTaskId, position,
    });
  }

  async function handleTaskDropOnWs(wsId: string) {
    const draggedId = tDragId;
    setTDragId(null);
    setTDropTaskId(null);
    setTDropPos(null);
    setTDropWsId(null);
    if (!draggedId) return;
    await doTaskMove(draggedId, wsId, { kind: 'end' });
  }

  async function handleEditClaudeUrl() {
    const current = project?.claudeProjectUrl || '';
    const next = window.prompt(
      'Claude Project URL (paste the link to the related Claude project):',
      current,
    );
    if (next === null) return; // cancelled
    const trimmed = next.trim();
    try {
      await updateProject(projectId, { claudeProjectUrl: trimmed ? trimmed : null });
    } catch (err: any) {
      setError(err?.message || 'Could not save Claude Project link.');
    }
  }

  async function handleEditFilesUrl() {
    const current = project?.filesUrl || '';
    const next = window.prompt(
      "Files URL (paste the link to the project's Google Drive folder):",
      current,
    );
    if (next === null) return; // cancelled
    const trimmed = next.trim();
    try {
      await updateProject(projectId, { filesUrl: trimmed ? trimmed : null });
    } catch (err: any) {
      setError(err?.message || 'Could not save Files link.');
    }
  }

  // ---- Roadmap fields ----

  async function handleChangePriority(next: PriorityChoice) {
    try {
      if (next === 'done') {
        // Mark complete. Clear any backlog flag so the project lives in
        // exactly one rail (Done), never both.
        await updateProject(projectId, { done: true, backlogged: null });
      } else {
        // Any real priority — or Unprioritized (null) — reopens a done
        // project as it sets its active lane.
        await updateProject(projectId, { priority: next, done: null });
      }
    } catch (err: any) {
      setError(err?.message || 'Could not save priority.');
    }
  }

  async function handleReopen() {
    try {
      await updateProject(projectId, { done: null });
    } catch (err: any) {
      setError(err?.message || 'Could not reopen project.');
    }
  }

  async function handleChangeDepartment(next: ProjectDepartment | null) {
    try {
      await updateProject(projectId, { department: next });
    } catch (err: any) {
      setError(err?.message || 'Could not save department.');
    }
  }

  async function handleChangeHealth(next: ProjectHealth | null) {
    try {
      await updateProject(projectId, { manualHealth: next });
    } catch (err: any) {
      setError(err?.message || 'Could not save health.');
    }
  }

  async function handleToggleBacklog() {
    if (!project) return;
    try {
      await updateProject(projectId, { backlogged: !project.backlogged });
    } catch (err: any) {
      setError(err?.message || 'Could not update backlog state.');
    }
  }

  async function handleChangeStartDate(value: string) {
    // Empty string clears the date. The native <input type="date"> emits
    // 'YYYY-MM-DD' on change.
    if (!value) {
      try { await updateProject(projectId, { startDate: null }); }
      catch (err: any) { setError(err?.message || 'Could not save start date.'); }
      return;
    }
    const ms = dateInputToMillis(value);
    if (ms === null) return;
    try {
      await updateProject(projectId, { startDate: ms });
    } catch (err: any) {
      setError(err?.message || 'Could not save start date.');
    }
  }

  async function handleSaveAsTemplate() {
    if (!project) return;
    const confirmed = window.confirm(
      `Save "${project.title}" as a template?\n\n` +
        `Captures workstreams and the task list (titles + descriptions).\n` +
        `Excludes statuses, assignees, dates, and comments.`,
    );
    if (!confirmed) return;
    try {
      const templateId = await saveProjectAsTemplate(project, workstreams, tasks, uid);
      history.push(`/Projects/Templates/${templateId}`);
    } catch (err: any) {
      setError(err?.message || 'Could not save as template.');
    }
  }

  async function handleChangeTargetDate(value: string) {
    if (!value) {
      try { await updateProject(projectId, { targetDate: null }); }
      catch (err: any) { setError(err?.message || 'Could not save target date.'); }
      return;
    }
    const ms = dateInputToMillis(value);
    if (ms === null) return;
    try {
      await updateProject(projectId, { targetDate: ms });
    } catch (err: any) {
      setError(err?.message || 'Could not save target date.');
    }
  }

  // IonRouterOutlet can keep this page mounted in its stack even after the
  // user navigates back to /Projects or to a tab path. Bail out when the
  // URL clearly isn't a project detail — empty projectId means we're on
  // /Projects itself, and the literal tab segments belong to ProjectsList.
  // Without this, a stale "Project not found" briefly flashes on top of
  // the real tab page.
  if (!projectId || PROJECT_TAB_SEGMENTS.has(projectId)) {
    return null;
  }

  if (authLoading) {
    return (
      <PmSuiteLayout
        active="projects"
        onSectionChange={(id) => {
          // From ProjectDetail, switching the sub-rail is a real cross-route
          // navigation back to the projects list — not a within-page state
          // change. history.push is the right call here.
          if (id === 'mywork') history.push('/Projects/MyWork');
          else if (id === 'templates') history.push('/Projects/Templates');
          else history.push('/Projects');
        }}
      >
        <div className="pm-loading">Loading…</div>
      </PmSuiteLayout>
    );
  }

  if (!project) {
    return (
      <PmSuiteLayout
        active="projects"
        onSectionChange={(id) => {
          // From ProjectDetail, switching the sub-rail is a real cross-route
          // navigation back to the projects list — not a within-page state
          // change. history.push is the right call here.
          if (id === 'mywork') history.push('/Projects/MyWork');
          else if (id === 'templates') history.push('/Projects/Templates');
          else history.push('/Projects');
        }}
      >
        <div className="pm-page">
          <div className="pm-breadcrumb">
            <button type="button" className="pm-link-button" onClick={() => history.push('/Projects')}>Projects</button>
          </div>
          <div className="pm-empty">
            <h3>Project not found</h3>
            <p>It may have been deleted, or you don't have access.</p>
            <button className="pm-button" onClick={() => history.push('/Projects')}>Back to projects</button>
          </div>
        </div>
      </PmSuiteLayout>
    );
  }

  return (
    <PmSuiteLayout
      active="projects"
      onSectionChange={(id) => {
        if (id === 'mywork') history.push('/Projects/MyWork');
        else if (id === 'templates') history.push('/Projects/Templates');
        else history.push('/Projects');
      }}
    >
      <div className="pm-page">
        <div className="pm-breadcrumb">
          <div className="pm-breadcrumb-trail">
            <button type="button" className="pm-link-button" onClick={() => history.push('/Projects')}>Projects</button>
            {' / '}
            <span>{project.title}</span>
          </div>
          {/* ViewSelect moved down to the view-bar row above the workstreams,
              matching the design — keeps List/Kanban/Gantt close to the
              content they govern instead of stranded at the page header. */}
        </div>

            <div className="pm-header">
              <div className="pm-header-titles">
                <EditableTitle
                  value={project.title}
                  onCommit={async (next) => {
                    if (next.trim() === project.title) return;
                    try { await updateProject(projectId, { title: next.trim() }); }
                    catch (err: any) { setError(err?.message || 'Could not save title.'); }
                  }}
                />
                <EditableDescription
                  value={project.description || ''}
                  onCommit={async (next) => {
                    const trimmed = next.trim();
                    if (trimmed === (project.description || '')) return;
                    try { await updateProject(projectId, { description: trimmed }); }
                    catch (err: any) { setError(err?.message || 'Could not save description.'); }
                  }}
                  linkify={linkify}
                />
              </div>
              {/* Top action row — order matches the design:
                  Move to backlog → Claude Project → Drive files → Save as template.
                  Delete project moved to the page footer (bottom-right) so a
                  destructive action isn't the loudest button up top. */}
              <div className="pm-header-actions">
                <button
                  type="button"
                  className={`pm-button pm-button-ghost${project.backlogged ? ' is-on' : ''}`}
                  onClick={handleToggleBacklog}
                  title={project.backlogged ? 'Activate this project' : 'Park this project in the backlog'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="21 8 21 21 3 21 3 8" />
                    <rect x="1" y="3" width="22" height="5" />
                    <line x1="10" y1="12" x2="14" y2="12" />
                  </svg>
                  {project.backlogged ? 'In backlog · Activate' : 'Move to backlog'}
                </button>

                <div className="pm-extlink-group">
                  {project.claudeProjectUrl ? (
                    <a
                      href={project.claudeProjectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pm-button pm-button-ghost"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      Claude Project
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="pm-button pm-button-ghost"
                      onClick={handleEditClaudeUrl}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      Claude Project
                    </button>
                  )}
                  {project.claudeProjectUrl ? (
                    <button
                      type="button"
                      className="pm-link-button pm-extlink-edit"
                      onClick={handleEditClaudeUrl}
                      title="Edit Claude Project URL"
                    >
                      Edit link
                    </button>
                  ) : null}
                </div>

                <div className="pm-extlink-group">
                  {project.filesUrl ? (
                    <a
                      href={project.filesUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pm-button pm-button-ghost"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                      Drive files
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="pm-button pm-button-ghost"
                      onClick={handleEditFilesUrl}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                      Drive files
                    </button>
                  )}
                  {project.filesUrl ? (
                    <button
                      type="button"
                      className="pm-link-button pm-extlink-edit"
                      onClick={handleEditFilesUrl}
                      title="Edit Files URL"
                    >
                      Edit link
                    </button>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="pm-button pm-button-ghost"
                  onClick={handleSaveAsTemplate}
                  title="Capture this project's workstreams and task list as a reusable template"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="1" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="9" y1="9" x2="9" y2="21" />
                  </svg>
                  Save as template
                </button>
                {/* Move-to-backlog already lives at the start of this row.
                    Delete project moved to the bottom-right footer. */}
              </div>
            </div>

            {project.backlogged ? (
              <div className="pm-backlog-banner" role="status">
                <div>
                  <strong>In backlog.</strong> This project is parked and won't appear on the Grid or Roadmap.
                  Auto health signals are paused.
                </div>
                <button
                  type="button"
                  className="pm-button pm-button-primary pm-button-sm"
                  onClick={handleToggleBacklog}
                >
                  Activate
                </button>
              </div>
            ) : null}

            {project.done ? (
              <div className="pm-done-banner" role="status">
                <div>
                  <strong>Done.</strong> This project is complete and lives in the Done drawer.
                  It's hidden from the Grid and Roadmap, and auto health signals are paused.
                </div>
                <button
                  type="button"
                  className="pm-button pm-button-primary pm-button-sm"
                  onClick={handleReopen}
                >
                  Reopen
                </button>
              </div>
            ) : null}

            {/* Meta strip — horizontal row, labels stacked above values.
                Matches the design's .meta block: Priority • Start • Target
                • Department [spacer] Health. (Owner/Team rendered separately
                in the future — project doesn't track its own owner today.) */}
            <div className="pm-meta" aria-label="Project meta">
              <div className="pm-meta-group">
                <div className="pm-meta-lbl">Priority</div>
                <ProjectPriorityPicker
                  priority={project.priority}
                  done={project.done}
                  onChange={handleChangePriority}
                />
              </div>
              <div className="pm-meta-group">
                <div className="pm-meta-lbl">Start</div>
                <input
                  type="date"
                  className="pm-meta-date"
                  value={typeof project.startDate === 'number' ? millisToDateInput(project.startDate) : ''}
                  onChange={(e) => handleChangeStartDate(e.target.value)}
                />
              </div>
              <div className="pm-meta-group">
                <div className="pm-meta-lbl">Target</div>
                <input
                  type="date"
                  className="pm-meta-date"
                  value={typeof project.targetDate === 'number' ? millisToDateInput(project.targetDate) : ''}
                  onChange={(e) => handleChangeTargetDate(e.target.value)}
                />
              </div>
              <div className="pm-meta-group">
                <div className="pm-meta-lbl">Department</div>
                <DepartmentPicker
                  department={project.department}
                  onChange={handleChangeDepartment}
                />
              </div>
              <div className="pm-meta-spacer" />
              <div className="pm-meta-group">
                <div className="pm-meta-lbl">Health</div>
                <HealthPill
                  health={computeHealth(project, projectTasks)}
                  onSetManual={handleChangeHealth}
                />
              </div>
            </div>

            <StatCards tasks={projectTasks} />

            {/* View-switcher bar — sits directly above the workstreams.
                Left: List/Kanban/Gantt segmented tabs (moved down from the
                breadcrumb so the selector is adjacent to the content it
                governs). Right: Filter popout (owner + status) + Add task.
                Matches the design's view switcher + filter + add row. */}
            <div className="pm-view-bar">
              <ViewSelect value={view} onChange={setView} />
              <div className="pm-view-bar-actions">
                <ProjectFilterMenu
                  admins={admins}
                  ownerFilter={ownerFilter}
                  statusFilter={statusFilter}
                  onOwnerChange={setOwnerFilter}
                  onStatusChange={setStatusFilter}
                />
                {/* Add task is contextual to the active workstream and is
                    handled inline within each workstream card today, so this
                    page-level button is a visual placeholder for now. */}
                <button
                  type="button"
                  className="pm-button pm-button-primary pm-button-sm"
                  disabled
                  title="Add a task to the topmost workstream — coming next"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add task
                </button>
              </div>
            </div>

            {archivedCount > 0 ? (
              <div className="pm-archive-toggle">
                <button
                  type="button"
                  className="pm-link-button"
                  onClick={() => setShowArchived((v) => !v)}
                >
                  {showArchived
                    ? `Hide archived (${archivedCount})`
                    : `Show archived (${archivedCount})`}
                </button>
              </div>
            ) : null}

            {error ? <div className="pm-error">{error}</div> : null}

            {view === 'list' ? (
              projectWorkstreams.length === 0 ? (
                <div className="pm-empty">
                  <h3>
                    {allProjectWorkstreams.length === 0
                      ? 'No workstreams yet'
                      : 'No active workstreams'}
                  </h3>
                  <p>
                    {allProjectWorkstreams.length === 0
                      ? 'Add one below to start organizing tasks.'
                      : 'All workstreams in this project are archived. Toggle "Show archived" above to see them.'}
                  </p>
                </div>
              ) : (
                projectWorkstreams.map((ws) => (
                  <WorkstreamCard
                    key={ws.id}
                    workstream={ws}
                    tasks={filteredTasksByWorkstream[ws.id] || []}
                    allTasksInWorkstream={tasksByWorkstreamUnfiltered[ws.id] || []}
                    admins={admins}
                    currentUid={uid}
                    isDragging={dragId === ws.id}
                    dropIndicator={dropId === ws.id ? dropPos : null}
                    onDragStartWs={handleWsDragStart}
                    onDragOverWs={handleWsDragOver}
                    onDragLeaveWs={handleWsDragLeave}
                    onDropWs={handleWsDrop}
                    onDragEndWs={handleWsDragEnd}
                    // Task drag (cross-workstream)
                    dragKind={dragId ? 'ws' : tDragId ? 'task' : null}
                    taskDragId={tDragId}
                    taskDropTaskId={tDropTaskId}
                    taskDropPos={tDropPos}
                    isTaskDropTargetWs={tDropWsId === ws.id}
                    onTaskDragStart={handleTaskDragStart}
                    onTaskDragOverTask={handleTaskDragOverTask}
                    onTaskDragLeaveTask={handleTaskDragLeaveTask}
                    onTaskDragOverWs={handleTaskDragOverWs}
                    onTaskDragLeaveWs={handleTaskDragLeaveWs}
                    onTaskDropOnTask={handleTaskDropOnTask}
                    onTaskDropOnWs={handleTaskDropOnWs}
                    onTaskDragEnd={handleTaskDragEnd}
                    highlightedTaskId={targetTaskId}
                  />
                ))
              )
            ) : view === 'kanban' ? (
              <KanbanView
                tasks={filteredTasks}
                workstreams={projectWorkstreams}
                admins={admins}
                currentUid={uid}
                projectId={projectId}
                highlightedTaskId={targetTaskId}
              />
            ) : (
              <GanttView
                tasks={filteredTasks}
                workstreams={projectWorkstreams}
                admins={admins}
                projectId={projectId}
              />
            )}

        {view === 'list' ? (
          <form className="pm-create-form" onSubmit={handleAddWorkstream} style={{ marginTop: 8 }}>
            <input
              placeholder="New workstream title"
              value={newWorkstream}
              onChange={(e) => setNewWorkstream(e.target.value)}
            />
            <div>
              <button className="pm-button pm-button-primary" type="submit" disabled={addingWs || !newWorkstream.trim()}>
                {addingWs ? 'Adding…' : 'Add workstream'}
              </button>
            </div>
          </form>
        ) : null}

        {/* Destructive footer — bottom-right Delete project. Pulled out of the
            top action row so the loudest button isn't the destructive one. */}
        <div className="pm-detail-footer">
          <button
            type="button"
            className="pm-link-button pm-detail-delete"
            onClick={handleDeleteProject}
          >
            Delete project
          </button>
        </div>
      </div>
    </PmSuiteLayout>
  );
};

export default ProjectDetail;
